import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { DocManiaLogo, DocManiaLogoImage } from '../DocManiaLogo';
import { LogOut, Settings, User } from 'lucide-react';
import {
  DOCUMENT_NAV_ITEMS,
  TOOL_NAV_ITEMS,
  isNavItemActive,
} from '../../lib/dashboardNavConfig';

function NavLink({ item, onNavigate }) {
  const location = useLocation();
  const active = isNavItemActive(location.pathname, item.route);

  return (
    <Link
      to={item.route}
      onClick={onNavigate}
      data-testid={`sidebar-nav-${item.id}`}
      className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-base font-medium transition-colors ${
        active
          ? 'bg-primary/10 text-foreground'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
      }`}
    >
      <i className={`ti ${item.icon} text-base shrink-0 ${item.iconColor}`} aria-hidden />
      <span className="leading-snug break-words">{item.label}</span>
    </Link>
  );
}

function SidebarBody({ user, onLogout, onNavigate }) {
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-4 pt-5 pb-4">
        <Link to="/dashboard" onClick={onNavigate} className="mb-6 flex items-center gap-2.5 px-1">
          <DocManiaLogoImage size={32} />
          <DocManiaLogo size="small" />
        </Link>

        <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Create Documents
        </p>
        <nav className="mb-5 space-y-0.5" aria-label="Documents">
          {DOCUMENT_NAV_ITEMS.map((item) => (
            <NavLink key={item.id} item={item} onNavigate={onNavigate} />
          ))}
        </nav>

        <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Tools &amp; Utilities
        </p>
        <nav className="mb-5 space-y-0.5" aria-label="Tools and utilities">
          {TOOL_NAV_ITEMS.map((item) => (
            <NavLink key={item.id} item={item} onNavigate={onNavigate} />
          ))}
        </nav>

        <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Store
        </p>
        <nav className="space-y-0.5" aria-label="Templates and library">
          <NavLink
            item={{ id: 'templates_store', label: 'Templates & Toolkits', route: '/templates', icon: 'ti-package', iconColor: 'text-indigo-600 dark:text-indigo-400' }}
            onNavigate={onNavigate}
          />
          <NavLink
            item={{ id: 'my_library', label: 'My Library', route: '/my-library', icon: 'ti-books', iconColor: 'text-emerald-600 dark:text-emerald-400' }}
            onNavigate={onNavigate}
          />
        </nav>
      </div>

      <div className="border-t border-border px-4 py-4 space-y-3">
        <div className="p-3 rounded-xl bg-muted/50" data-testid="sidebar-account-block">
          <div className="flex items-center gap-2 mb-1">
            <User className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium truncate text-foreground text-sm">{user?.full_name}</span>
          </div>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>

        {isAdmin && (
          <Link to="/admin" onClick={onNavigate}>
            <Button variant="outline" className="w-full justify-start h-9 text-sm" data-testid="nav-admin">
              <Settings className="mr-2 h-4 w-4" />
              Admin Dashboard
            </Button>
          </Link>
        )}

        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground h-9 text-sm"
          onClick={onLogout}
          data-testid="logout-btn"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );
}

/** Desktop sidebar — fixed 220px. */
export function DashboardSidebarDesktop({ user, onLogout }) {
  return (
    <aside
      className="fixed left-0 top-0 bottom-0 z-40 hidden md:flex w-[220px] flex-col border-r border-border bg-card"
      data-testid="dashboard-sidebar-desktop"
    >
      <SidebarBody user={user} onLogout={onLogout} />
    </aside>
  );
}

/** Mobile drawer body (used inside Sheet). */
export function DashboardSidebarMobileContent({ user, onLogout, onClose }) {
  return (
    <div className="h-full" data-testid="dashboard-sidebar-mobile">
      <SidebarBody user={user} onLogout={onLogout} onNavigate={onClose} />
    </div>
  );
}
